import {
  CrossClientQueryEvaluationTypes,
  type CrossClientQueryEvaluationType,
  type CrossClientQueryExecution,
  type CrossClientQueryResult,
  type CrossClientResultTab,
} from '../../../models/CrossClient'
import { canLocalEvaluateForTab, evaluationTypeForTab } from '../../../store/crossClient/crossClientResults'
import { type IntegrationServiceId } from '../../../common/integrations'
import { getCrossClientIntegrationDisplayName } from '../integrationDisplay'

export interface CrossClientNextStep {
  kind: CrossClientQueryEvaluationType
  canLocal: boolean
  breadcrumb: string[]
  tooltip: string
  integrations: string
}

const integrationDisplayNames = (execution: CrossClientQueryExecution | null): string[] => {
  if (!execution) {
    return []
  }
  const seen = new Set<string>()
  const names: string[] = []
  for (const source of execution.sources) {
    if (source.type !== 'integration' || seen.has(source.integrationType)) {
      continue
    }
    seen.add(source.integrationType)
    names.push(getCrossClientIntegrationDisplayName(source.integrationType as IntegrationServiceId))
  }
  return names
}

const formatIntegrationList = (names: string[]): string =>
  names.length === 0 ? 'your connected integrations' : new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(names)

export const crossClientIntegrationList = (execution: CrossClientQueryExecution | null): string => formatIntegrationList(integrationDisplayNames(execution))

export const getCrossClientNextStep = (
  tab: CrossClientResultTab,
  result: CrossClientQueryResult,
  execution: CrossClientQueryExecution | null,
): CrossClientNextStep => {
  const kind = evaluationTypeForTab(tab, result.lastResolverStage, execution)
  const canLocal = canLocalEvaluateForTab(tab, execution)
  const integrations = formatIntegrationList(integrationDisplayNames(execution))

  if (kind === CrossClientQueryEvaluationTypes.LOCAL_RESOLVER) {
    return {
      kind,
      canLocal,
      breadcrumb: ['Client Profile', 'Check integrations'],
      tooltip: `I'll pull data from ${integrations} to help confirm this client.`,
      integrations,
    }
  }

  return {
    kind,
    canLocal,
    breadcrumb: canLocal ? ['Client Profile', 'Integrations', 'Full review'] : ['Client Profile', 'Full review'],
    tooltip: "I'll gather everything across meetings, notes and integrations for each client and analyze it together.",
    integrations,
  }
}

export const crossClientNextStepLeadCopy = (nextStep: CrossClientNextStep, firstName: string): string => {
  const { kind, canLocal, integrations } = nextStep
  const integrationsChecked = kind === CrossClientQueryEvaluationTypes.AI && canLocal
  const triedCopy = integrationsChecked
    ? `I checked ${integrations} but couldn't confirm ${firstName} yet.`
    : `I couldn't confirm ${firstName} based on the Client Profile in Zocks.`
  const nextCopy =
    kind === CrossClientQueryEvaluationTypes.LOCAL_RESOLVER
      ? `I can pull data from ${integrations} to help confirm ${firstName}.`
      : `I can gather everything across ${firstName}'s meetings, notes and records to find out more.`
  return `${triedCopy} ${nextCopy}`
}
